import _ from 'underscore';
import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Stack,
  Text,
  IconButton,
  FontIcon,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getItemStyles, getHeaderStyles, getDetailsStyles, getSummaryStyle } from './get-styles';

const AccordionContext = React.createContext({});

const Item = React.forwardRef((props, ref) => {
  const {
    isExpanded,
    isBlocking,
    onToggle,
    children,
    isToggleHidden,
  } = props;
  const classes = useDefaultStyles(getItemStyles);

  const contextValue = useMemo(() => ({
    isBlocking,
    isExpanded,
    onToggle,
    isToggleHidden,
  }), [isBlocking, isExpanded, onToggle, isToggleHidden]);

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={classNames(classes.accordionItem, 'ba-accordion-item')} ref={ref}>
        <Stack>
          {children}
        </Stack>
      </div>
    </AccordionContext.Provider>
  );
});

Item.propTypes = {
  onToggle: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isToggleHidden: PropTypes.bool,
  isBlocking: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

Item.defaultProps = {
  children: null,
  isBlocking: false,
  isToggleHidden: false,
};

const Header = (props) => {
  const {
    title,
  } = props;
  const {
    isExpanded,
    onToggle,
    isToggleHidden,
  } = useContext(AccordionContext);
  const classes = useDefaultStyles(getHeaderStyles);

  const iconProps = useMemo(() => {
    const transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)';

    return {
      iconName: 'ChevronUp',
      styles: {
        root: {
          transform,
          transformOrigin: '50% 50%',
          transition: 'transform .1s linear',
        },
      },
    };
  }, [isExpanded]);

  return (
    <div className="ba-accordion-header">
      <Stack horizontal horizontalAlign="space-between">
        <Text className={classNames('ba-accordion-title', classes.title)}>{title}</Text>
        {
          !isToggleHidden &&
          <IconButton
            iconProps={iconProps}
            onClick={onToggle}
            className={classNames({
              'ba-accordion-toggle': true,
              'is-expanded': isExpanded,
            })}
          />
        }
      </Stack>
    </div>
  );
};

Header.propTypes = {
  title: PropTypes.string.isRequired,
};

Header.defaultProps = {
};

const Summary = (props) => {
  const {
    errorMessage,
    children,
  } = props;
  const classes = useDefaultStyles(getSummaryStyle);
  const {
    isExpanded,
  } = useContext(AccordionContext);

  if (isExpanded) {
    return null;
  }

  return (
    <div className="ba-accordion-summary">
      {!_.isEmpty(errorMessage) ?
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <FontIcon
            iconName="Info"
            className={classNames('error-icon', classes.icon)}
          />
          <Text className={classNames('error-message', classes.error)}>{errorMessage}</Text>
        </Stack> : children}
    </div>
  );
};

Summary.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  errorMessage: PropTypes.string,
};

Summary.defaultProps = {
  children: null,
  errorMessage: undefined,
};

const Details = (props) => {
  const {
    children,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getDetailsStyles);

  const {
    isExpanded,
    isBlocking,
  } = useContext(AccordionContext);

  if (!isExpanded) {
    return null;
  }

  return (
    <div className={classNames(classes.accordionDetails, 'ba-accordion-details')}>
      <div className="ba-accordion-details-content">
        {children}
      </div>
      {
        isBlocking &&
        <Spinner
          size={SpinnerSize.large}
          label={i18n.getString(_TL_('Loading...'))}
          className={classNames(classes.spinner, 'ba-accordion-blocker')}
        />
      }
    </div>
  );
};

Details.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

Details.defaultProps = {
  children: null,
};

export const Accordion = Object.seal({
  Item,
  Header,
  Summary,
  Details,
});
